import {reduce} from 'lodash';
import React, {useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import classNames from 'classnames';

import {scrolledToScore} from 'shared/portfolio/actions/test-scores';
import {TEST_SCORE_NAME_MAP} from './test-score-form';
import {TestScore as TestScoreType} from './test-scores-form-wrapper';

import Text from 'shared/components/text';
import {MONTH_ABBRVS} from 'shared/utils';
import ScoreNodes from './score-nodes';

interface Props {
  testScore: TestScoreType;
  hover?: boolean;
}

const TestScore = ({
  testScore: {scrollTo, _id, month, year, type, subtype, scores, maxScore},
  hover,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (scrollTo) {
      setTimeout(() => {
        ref.current?.scrollIntoView({behavior: 'smooth'});
        dispatch(scrolledToScore(_id));
      }, 550);
    }
  }, [_id, dispatch, scrollTo]);

  const typeToDisplay = () => {
    if (type === 'ap') {
      return `AP ${subtype.name || subtype.subject}`;
    } else if (type && TEST_SCORE_NAME_MAP[type]) {
      return TEST_SCORE_NAME_MAP[type];
    } else if (type === 'other') {
      return subtype.abbreviation || subtype.name;
    } else {
      return 'Test';
    }
  };

  const ignoredTests = ['satScoreEssay', 'satScoreMultipleChoice', 'act2025ScoreScience'];
  const otherTest = type === 'other';

  let total = reduce(
    Object.keys(scores),
    (sum, scoreName) => (!ignoredTests.includes(scoreName) ? sum + scores[scoreName] : sum),
    0
  );

  if (type === 'preAct' || type === 'actPlan' || type === 'act') {
    total = Math.round(total / 4);
  } else if (type === 'act2025') {
    total = Math.round(total / 3);
  }

  return (
    <div className="test-score-card" ref={ref}>
      <div className="portfolio-sections header-container larger-left-side">
        <div className="portfolio-sections card-header">
          <Text header emphasis inline weight={4} hover={hover}>
            {typeToDisplay()}
          </Text>
          <Text weight={5} hover={hover}>
            {MONTH_ABBRVS[month]} {year}
          </Text>
        </div>
        {maxScore && (
          <div
            className={classNames('test-score-card-score-range', {hover, 'other-test': otherTest})}
          >
            {total + ' / '}
            <span className="test-score-card-total">{maxScore}</span>
          </div>
        )}
      </div>
      <div className={classNames('portfolio-sections test-score-scores', {other: otherTest})}>
        <Text weight={5} hover={hover} color="darkgrey">
          {type === 'other' && subtype.abbreviation ? subtype.name : ''}
        </Text>
        <div className={classNames('test-score-card-nodes', {'other-test': otherTest})}>
          <ScoreNodes _id={_id} type={type} scores={scores} hover={hover} />
        </div>
      </div>
    </div>
  );
};

export default TestScore;
